import React, { FC } from "react";
import styled from "styled-components";

interface IButton {
  children: JSX.Element | JSX.Element[] | string;
  color?: string;
  type?: "button" | "submit" | "reset";
  size?: "sm" | "l";
  isDisabled?: boolean;
  isOutlined?: boolean;
  borderColor?: string;
  textColor?: string;
  backgroundColor?: string;
  isBlock?: boolean;
  buttonType?: string;
  onClick?: (e?: MouseEvent) => void;
  ariaLabel?: string;
}

interface IButtonStyled {
  isDisabled?: boolean;
  isBlock?: boolean;
  buttonType?: string;
  isOutlined?: boolean;
  borderColor?: string;
  textColor?: string;
  backgroundColor?: string;
  size?: "sm" | "l";
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const StyledButton = styled.button<IButtonStyled>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ isBlock }) => (isBlock && "center") || "center"};
  transition: 0.4s;
  width: ${({ isBlock }) => (isBlock ? "100%" : "200px")};
  border-radius: 5px !important;
  font-family: var(--font-brand);
  font-weight: 500!important;
  font-size: 16px!important;
  background-color: var(--${({ backgroundColor }) => backgroundColor});
  border: 1px solid var(--${({ borderColor }) => borderColor});

  box-shadow: rgb(0 0 0 / 15%) 0px 0px 40px;
  padding: ${({ size }) => (size === "l" ? "12px 21px" : "4px 9px")};
  line-height: 1.5em!important;
  color: var(--${({ textColor }) => textColor});

  &:disabled {
    cursor: not-allowed;
    background-color: var(--color-disabled);
  }

  &:not(:disabled):hover {
    color: var(--${({ textColor }) => textColor});
  }

  &:not(:disabled):focus {
    color: var(--${({ textColor }) => textColor});
    box-shadow: var(--box-shadow-focus);
  }

  ${({ buttonType }) =>
    buttonType === "secondary" &&
    `
  border-color: black;
  background-color: white;
  color: black;
  &:disabled {
    cursor: not-allowed;
    background-color: var(--color-disabled);
  }

  &:not(:disabled):hover {
    color: black;
    background-color: white;
    border-color: black;
  }

  &:not(:disabled):focus {
    color: black;
    background-color: white;
    border-color: black;
  }
`}
`;

const StyledText = styled.span`
  color: inherit;
  pointer-events: none;
`;

const Button: FC<IButton> = (props): JSX.Element => {
  const {
    children,
    isDisabled,
    isOutlined = false,
    type = "button",
    isBlock,
    borderColor = "button-border",
    backgroundColor = "button",
    textColor = "button-text",
    buttonType = "primary",
    size = "l",
    ariaLabel,
    onClick,
  } = props;

  const handleButtonClick = () => {
    if (onClick && !isDisabled) {
      onClick();
    }
  };

  return (
    <StyledButton
      size={size}
      isBlock={isBlock}
      type={type}
      disabled={isDisabled}
      isOutlined={isOutlined}
      borderColor={borderColor}
      textColor={textColor}
      backgroundColor={backgroundColor}
      onClick={handleButtonClick}
      buttonType={buttonType}
      aria-label={ariaLabel}
    >
      <StyledText>{children}</StyledText>
    </StyledButton>
  );
};

export default Button;
